/**
 * Páginas de retorno post-pago Klap.
 *
 * /payment/success — con **safety net de reconciliación**: si el webhook aún no
 *                    ha marcado la orden como approved, llamamos a
 *                    /api/klap/order-status/{id} que consulta Klap y actualiza.
 * /payment/failure — mensaje de error + link para reintentar.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { CheckCircle2, XCircle, Loader2, ArrowRight, Home, RotateCw } from 'lucide-react';
import { API } from '../utils/api';

// ============================================================
// SUCCESS
// ============================================================
export function KlapPaymentSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState('loading'); // 'loading' | 'approved' | 'pending' | 'error'
  const [orderInfo, setOrderInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const orderId = params.get('order_id') || (typeof localStorage !== 'undefined' ? localStorage.getItem('current_order_id') : null);

  useEffect(() => {
    let alive = true;
    async function verify() {
      if (!orderId) {
        setState('error');
        setErrorMsg('No se encontró el ID de la orden. Por favor contacta a contacto@sinadicciones.cl');
        return;
      }
      try {
        // Safety net: consulta a Klap y reconcilia si es necesario
        const r = await axios.get(`${API}/klap/order-status/${encodeURIComponent(orderId)}`, {
          timeout: 15000,
        });
        if (!alive) return;
        const status = String(r.data?.status || '').toLowerCase();
        setOrderInfo(r.data);
        if (['approved', 'completed', 'paid'].includes(status)) {
          setState('approved');
          try {
            if (typeof window !== 'undefined' && window.gtag) {
              window.gtag('event', 'purchase', {
                transaction_id: orderId,
                currency: 'CLP',
                payment_provider: 'klap',
              });
            }
          } catch (_gtagErr) { /* noop */ }
          // Limpiar localStorage
          try {
            localStorage.removeItem('current_order_id');
            localStorage.removeItem('current_order_provider');
          } catch (_lsErr) { /* noop */ }
        } else {
          setState('pending');
        }
      } catch (err) {
        if (!alive) return;
        setState('error');
        setErrorMsg(err?.response?.data?.detail || err?.message || 'Error consultando el pago.');
      }
    }
    verify();
    return () => { alive = false; };
  }, [orderId]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <title>Pago confirmado · SinAdicciones.org</title>
      <meta name="robots" content="noindex, nofollow" />
      <Header />
      <main className="max-w-2xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-8 md:p-12 text-center">
          {state === 'loading' && (
            <div data-testid="klap-success-loading">
              <Loader2 className="w-12 h-12 animate-spin text-blue-400 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-50 mb-2">Verificando tu pago…</h1>
              <p className="text-slate-400 text-sm">Solo tomará unos segundos.</p>
            </div>
          )}
          {state === 'approved' && (
            <div data-testid="klap-success-approved">
              <div className="w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-10 h-10 text-emerald-400" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-3">¡Pago confirmado! 🎉</h1>
              <p className="text-slate-300 mb-8">
                Tu pago se procesó correctamente. Te enviamos un email con el detalle a tu casilla.
              </p>
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 mb-8 text-left text-sm">
                <div className="text-emerald-300 font-semibold mb-2">Referencia de pago</div>
                <div className="text-slate-300 font-mono text-xs break-all">{orderId}</div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-colors"
                  data-testid="klap-success-home"
                >
                  <Home className="w-4 h-4" /> Volver al inicio
                </Link>
                <Link
                  to="/mi-cuenta"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-700 hover:border-slate-600 text-slate-200 font-semibold text-sm transition-colors"
                >
                  Mi cuenta <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          )}
          {state === 'pending' && (
            <div data-testid="klap-success-pending">
              <div className="w-20 h-20 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto mb-5">
                <Loader2 className="w-10 h-10 text-amber-300 animate-spin" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">Aún estamos confirmando tu pago</h1>
              <p className="text-slate-300 mb-2">
                Klap está procesando la transacción. Esto puede tomar hasta 1 minuto.
              </p>
              <p className="text-slate-500 text-sm mb-8">
                Referencia: <span className="font-mono">{orderId}</span>
              </p>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold text-sm transition-colors"
                data-testid="klap-success-retry"
              >
                <RotateCw className="w-4 h-4" /> Volver a verificar
              </button>
            </div>
          )}
          {state === 'error' && (
            <div data-testid="klap-success-error">
              <div className="w-20 h-20 rounded-full bg-rose-500/15 border border-rose-500/30 flex items-center justify-center mx-auto mb-5">
                <XCircle className="w-10 h-10 text-rose-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-3">No pudimos verificar tu pago</h1>
              <p className="text-slate-300 mb-2">{errorMsg || 'Error desconocido.'}</p>
              <p className="text-slate-500 text-sm mb-8">
                Si el cargo apareció en tu tarjeta, escríbenos a{' '}
                <a href="mailto:contacto@sinadicciones.cl" className="text-emerald-300 hover:underline">contacto@sinadicciones.cl</a>{' '}
                con la referencia <span className="font-mono">{orderId || 'sin-id'}</span>.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-700 hover:border-slate-600 text-slate-200 text-sm">
                <Home className="w-4 h-4" /> Volver al inicio
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

// ============================================================
// FAILURE
// ============================================================
export function KlapPaymentFailure() {
  const [params] = useSearchParams();
  const orderId = params.get('order_id') || (typeof localStorage !== 'undefined' ? localStorage.getItem('current_order_id') : null);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <title>Pago no completado · SinAdicciones.org</title>
      <meta name="robots" content="noindex, nofollow" />
      <Header />
      <main className="max-w-2xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-8 md:p-12 text-center" data-testid="klap-failure">
          <div className="w-20 h-20 rounded-full bg-rose-500/15 border border-rose-500/30 flex items-center justify-center mx-auto mb-5">
            <XCircle className="w-10 h-10 text-rose-400" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">El pago no se completó</h1>
          <p className="text-slate-300 mb-6">
            Puede haber sido rechazado por el banco, cancelado por ti o expirado. No se realizó ningún cargo a tu tarjeta.
          </p>
          {orderId && (
            <p className="text-slate-500 text-xs mb-8">
              Referencia: <span className="font-mono">{orderId}</span>
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/planes"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm"
              data-testid="klap-failure-retry"
            >
              <RotateCw className="w-4 h-4" /> Volver a intentar
            </Link>
            <Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-700 hover:border-slate-600 text-slate-200 text-sm">
              Ayuda
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default KlapPaymentSuccess;
